"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type TosVersion } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";

/**
 * EPIC-008 STORY-808 — public Terms of Service page.
 *
 * Renders the currently effective ToS body. Public route — readable
 * pre-signup so users see the terms before agreeing. The accept
 * action lives on the re-prompt banner / signup flow, not here.
 *
 * Body is markdown-ish (headings, paragraphs, lists). To avoid
 * pulling in a markdown lib, a tiny renderer below handles the
 * patterns we use in our seeded body. When the legal-review v2.0
 * lands and complexity grows, swap to react-markdown — well-scoped
 * change, single component.
 */
export default function TosPage() {
  const { t } = useTranslation();
  const [tos, setTos] = useState<TosVersion | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getCurrentTos()
      .then((row) => {
        if (!cancelled) setTos(row);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : t("tosPage.loadFailed"));
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SiteHeader />
      <main className="mx-auto max-w-[800px] px-5 py-8">
        <Link
          href="/help"
          className="text-[12px] text-[var(--color-text3)] hover:text-[var(--color-text)]"
        >
          {t("tosPage.backToHelp")}
        </Link>

        {error && (
          <p
            role="alert"
            className="mt-3 text-[12px]"
            style={{ color: "var(--color-no)" }}
          >
            {error}
          </p>
        )}

        {!tos && !error && (
          <p className="mt-6 text-[12px] text-[var(--color-text3)]">
            {t("tosPage.loading")}
          </p>
        )}

        {tos && (
          <article className="mt-3">
            <header className="mb-6 flex flex-wrap items-baseline justify-between gap-2 border-b border-[var(--color-border)] pb-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.4px] text-[var(--color-text3)]">
                  {t("tosPage.kicker")}
                </p>
                <h1 className="mt-0.5 text-[24px] font-extrabold tracking-tight">
                  {tos.version}
                </h1>
              </div>
              <p className="text-[11px] text-[var(--color-text3)]">
                {t("tosPage.effectivePrefix", {
                  date: new Date(tos.effectiveAt).toLocaleDateString(),
                })}
              </p>
            </header>

            <p className="mb-5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-3 text-[12px] text-[var(--color-text2)]">
              <strong>{t("tosPage.summaryLabel")}</strong> {tos.summary}
            </p>

            <TosBody bodyMd={tos.bodyMd} />
          </article>
        )}
      </main>
    </>
  );
}

/**
 * Tiny inline markdown renderer covering the patterns the seeded
 * body uses: # / ## headings, paragraphs, bold (**foo**), italics
 * (_foo_), underscore-italic, hr (---). Not a full CommonMark — when
 * the body grows past these patterns, swap to react-markdown.
 */
function TosBody({ bodyMd }: { bodyMd: string }) {
  const lines = bodyMd.split("\n");
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];
  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push(
      <p
        key={`p-${blocks.length}`}
        className="mb-3 text-[14px] leading-relaxed text-[var(--color-text2)]"
        dangerouslySetInnerHTML={{ __html: renderInline(para.join(" ")) }}
      />,
    );
    para = [];
  };
  for (const line of lines) {
    if (line.startsWith("# ")) {
      flushPara();
      blocks.push(
        <h1
          key={`h-${blocks.length}`}
          className="mt-6 mb-3 text-[20px] font-extrabold tracking-tight"
        >
          {line.slice(2)}
        </h1>,
      );
    } else if (line.startsWith("## ")) {
      flushPara();
      blocks.push(
        <h2
          key={`h-${blocks.length}`}
          className="mt-5 mb-2 text-[16px] font-bold"
        >
          {line.slice(3)}
        </h2>,
      );
    } else if (line.trim() === "---") {
      flushPara();
      blocks.push(
        <hr
          key={`hr-${blocks.length}`}
          className="my-5 border-[var(--color-border)]"
        />,
      );
    } else if (line.trim() === "") {
      flushPara();
    } else {
      para.push(line);
    }
  }
  flushPara();
  return <div>{blocks}</div>;
}

function renderInline(text: string): string {
  // Order matters: bold before italic so **_foo_** doesn't collapse.
  return text
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/_([^_]+)_/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}
